import React from "react";
import { Link } from "react-router-dom";
import { ArrowUp, MapPin, Mail, Phone } from "lucide-react";
import { useT, localizedPath } from "../i18n";
import Logo from "./Logo";

export default function Footer() {
  const { t, lang } = useT();

  const backTop = () => {
    if (window.__lenis) window.__lenis.scrollTo(0);
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <footer className="bg-[#1a211c] text-[#dfd8c9] pt-24 pb-10 grain" data-testid="site-footer">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-14">
          <div className="lg:col-span-5">
            <Logo variant="light" />
            <p className="mt-6 max-w-md text-[0.95rem] text-[#dfd8c9]/70 leading-relaxed">
              {t("footer.brief")}
            </p>
            <div className="mt-8 space-y-2 text-[0.85rem] text-[#dfd8c9]/60 font-mono">
              <div>{t("footer.legal.companyName")}</div>
              <div>{t("footer.legal.taxId")}</div>
              <div>{t("footer.legal.registration")}</div>
              <div>{t("footer.legal.license")}</div>
              <div>{t("footer.legal.jurisdiction")}</div>
              <div>{t("footer.legal.incorporated")}</div>
            </div>
          </div>

          <div className="lg:col-span-3">
            <div className="text-[0.72rem] uppercase tracking-[0.24em] text-[#c5b293] mb-5">
              {t("footer.columnCompany")}
            </div>
            <ul className="space-y-3">
              <li>
                <Link
                  to={localizedPath(lang)}
                  className="link-underline text-[0.95rem] text-[#f6f4ee]/85 hover:text-[#f6f4ee]"
                  data-testid="footer-link-home"
                >
                  {t("nav.home")}
                </Link>
              </li>
              <li>
                <a
                  href={`${localizedPath(lang)}#projects`}
                  className="link-underline text-[0.95rem] text-[#f6f4ee]/85 hover:text-[#f6f4ee]"
                  data-testid="footer-link-projects"
                >
                  {t("nav.projects")}
                </a>
              </li>
              <li>
                <a
                  href={`${localizedPath(lang)}#how-it-works`}
                  className="link-underline text-[0.95rem] text-[#f6f4ee]/85 hover:text-[#f6f4ee]"
                  data-testid="footer-link-how"
                >
                  {t("nav.howItWorks")}
                </a>
              </li>
              <li>
                <a
                  href={`${localizedPath(lang)}#contact`}
                  className="link-underline text-[0.95rem] text-[#f6f4ee]/85 hover:text-[#f6f4ee]"
                  data-testid="footer-link-contact"
                >
                  {t("nav.contact")}
                </a>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="text-[0.72rem] uppercase tracking-[0.24em] text-[#c5b293] mb-5">
              {t("footer.columnLegal")}
            </div>
            <ul className="space-y-3">
              <li>
                <Link
                  to={localizedPath(lang, "privacy-policy")}
                  className="link-underline text-[0.95rem] text-[#f6f4ee]/85 hover:text-[#f6f4ee]"
                  data-testid="footer-link-privacy"
                >
                  {t("nav.privacy")}
                </Link>
              </li>
              <li>
                <Link
                  to={localizedPath(lang, "cookie-policy")}
                  className="link-underline text-[0.95rem] text-[#f6f4ee]/85 hover:text-[#f6f4ee]"
                  data-testid="footer-link-cookies"
                >
                  {t("nav.cookies")}
                </Link>
              </li>
              <li>
                <Link
                  to={localizedPath(lang, "terms-conditions")}
                  className="link-underline text-[0.95rem] text-[#f6f4ee]/85 hover:text-[#f6f4ee]"
                  data-testid="footer-link-terms"
                >
                  {t("nav.terms")}
                </Link>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-2">
            <div className="text-[0.72rem] uppercase tracking-[0.24em] text-[#c5b293] mb-5">
              {t("footer.columnContact")}
            </div>
            <ul className="space-y-3 text-[0.9rem] text-[#f6f4ee]/85">
              <li className="flex items-start gap-2">
                <MapPin size={15} className="mt-1 text-[#c5b293]" />
                <span>{t("contact.address")}</span>
              </li>
              <li className="flex items-start gap-2">
                <Mail size={15} className="mt-1 text-[#c5b293]" />
                <a
                  href={`mailto:${t("contact.email")}`}
                  className="link-underline"
                  data-testid="footer-email"
                >
                  {t("contact.email")}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Phone size={15} className="mt-1 text-[#c5b293]" />
                <a
                  href={`tel:${t("contact.phone").replace(/\s/g, "")}`}
                  className="link-underline"
                  data-testid="footer-phone"
                >
                  {t("contact.phone")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-[#f6f4ee]/10 text-[0.78rem] text-[#dfd8c9]/55 max-w-4xl">
          {t("footer.riskNotice")}
        </div>

        <div className="mt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-[0.78rem] text-[#dfd8c9]/55 font-mono">{t("footer.rights")}</div>
          <button
            type="button"
            onClick={backTop}
            className="inline-flex items-center gap-2 text-[0.8rem] text-[#f6f4ee]/85 hover:text-[#f6f4ee] transition-colors"
            data-testid="footer-back-top"
          >
            <ArrowUp size={14} /> {t("footer.backTop")}
          </button>
        </div>
      </div>
    </footer>
  );
}
